// Composition capture + scoring — the pure logic behind ComposedExperienceSlide.
//
// Kept out of the React component so it is unit-testable and so the storage
// shape is defined in one place. Produces:
//   - per-block normalised [0,1] scores (actionable, scoreable blocks only),
//   - the experience rollup per the declared scoring mode (§3.4),
//   - block-scoped close tokens ({id.value}, {id.correct}, {id.delta}, …),
//   - the ONE composite capture payload persisted per experience (§3.4):
//     mirrors the existing {before, after} pattern but keyed by block id.
//
// Every consumer treats every block signal as OPTIONAL/nullable (the Phase-2
// superset rule): a block the learner didn't reach simply has no answer.
//
// Pure module — no React, no network. Safe to import anywhere.

import type {
  ChoiceBlock,
  CompositionBlock,
  CompositionScoring,
  FlagInTextBlock,
  RateSliderBlock,
  SelectEvidenceBlock,
  SortZonesBlock,
} from './types.js';

// ---------------------------------------------------------------------------
// Answer shapes (what the runtime records per block).
// ---------------------------------------------------------------------------

export type ChoiceAnswer = { optionId: string };
export type RateSliderAnswer = { value: number }; // also the confidence answer
export type SortZonesAnswer = { placement: Record<string, string> };
export type SelectEvidenceAnswer = { selected: string[] };
export type CommitBoxAnswer = { text: string };
export type FlagInTextAnswer = { flagged: string[] };
export type PerspectiveLensAnswer = { takes: Record<string, string> };

export type BlockAnswer =
  | ChoiceAnswer
  | RateSliderAnswer
  | SortZonesAnswer
  | SelectEvidenceAnswer
  | CommitBoxAnswer
  | FlagInTextAnswer
  | PerspectiveLensAnswer;

export type Answers = Record<string, BlockAnswer | undefined>;

export type CloseValues = Record<string, string | number | undefined>;

const clamp01 = (n: number): number => Math.max(0, Math.min(1, n));

// ---------------------------------------------------------------------------
// Per-block normalised score. Returns null when the block is unscoreable or the
// learner hasn't answered it.
// ---------------------------------------------------------------------------

export function scoreBlock(block: CompositionBlock, answer: BlockAnswer | undefined): number | null {
  if (!answer) return null;
  switch (block.type) {
    case 'choice':
      return scoreChoice(block, answer as ChoiceAnswer);
    case 'rate-slider':
      return scoreRateSlider(block, answer as RateSliderAnswer);
    case 'sort-zones':
      return scoreSortZones(block, answer as SortZonesAnswer);
    case 'select-evidence':
      return scoreSelectEvidence(block, answer as SelectEvidenceAnswer);
    case 'flag-in-text':
      return scoreFlagInText(block, answer as FlagInTextAnswer);
    default:
      return null; // stimulus, reveal, commit-box, confidence, perspective-lens never score
  }
}

function scoreChoice(block: ChoiceBlock, a: ChoiceAnswer): number | null {
  const correct = block.content.options.find((o) => o.correct === true);
  if (!correct) return null; // not a scored choice
  return a.optionId === correct.id ? 1 : 0;
}

function scoreRateSlider(block: RateSliderBlock, a: RateSliderAnswer): number | null {
  const target = block.structure.target;
  if (!target) return null; // captures a weighting, not graded
  const tol = Math.max(target.tolerance, 1e-9);
  const d = Math.abs(a.value - target.value);
  if (d <= tol) return 1;
  return clamp01(1 - (d - tol) / tol); // reaches 0 at 2× tolerance from target
}

function scoreSortZones(block: SortZonesBlock, a: SortZonesAnswer): number | null {
  const items = block.content.items;
  if (items.length === 0) return null;
  const correct = items.filter((it) => a.placement[it.id] === it.correctZone).length;
  return correct / items.length;
}

function scoreSelectEvidence(block: SelectEvidenceBlock, a: SelectEvidenceAnswer): number | null {
  const strong = block.content.items.filter((it) => it.strong === true);
  if (strong.length === 0) return null;
  const strongIds = new Set(strong.map((it) => it.id));
  const picked = new Set(a.selected);
  let hits = 0;
  let misses = 0;
  for (const id of picked) (strongIds.has(id) ? (hits += 1) : (misses += 1));
  return clamp01((hits - misses) / strong.length); // over-selecting distractors is penalised
}

function scoreFlagInText(block: FlagInTextBlock, a: FlagInTextAnswer): number | null {
  const flagged = block.content.segments.filter((s) => s.flagged === true);
  if (flagged.length === 0) return null;
  const flaggedIds = new Set(flagged.map((s) => s.id));
  const picked = new Set(a.flagged);
  let hits = 0;
  let misses = 0;
  for (const id of picked) (flaggedIds.has(id) ? (hits += 1) : (misses += 1));
  return clamp01((hits - misses) / flagged.length); // over-flagging honest text is penalised
}

// ---------------------------------------------------------------------------
// Experience rollup (§3.4).
// ---------------------------------------------------------------------------

export function rollupScore(
  blocks: CompositionBlock[],
  scoring: CompositionScoring,
  answers: Answers,
): number {
  const byId = new Map(blocks.map((b) => [b.id, b]));
  const parts: Array<{ score: number; weight: number }> = [];
  for (const entry of scoring.blocks) {
    const block = byId.get(entry.id);
    if (!block) continue;
    const s = scoreBlock(block, answers[entry.id]);
    if (s === null) continue; // unanswered → excluded (optional-signal rule)
    parts.push({ score: s, weight: scoring.mode === 'weighted' ? entry.weight ?? 1 : 1 });
  }
  if (parts.length === 0) return 0;
  if (scoring.mode === 'single') return parts[0].score;
  const totalWeight = parts.reduce((acc, p) => acc + p.weight, 0) || 1;
  return parts.reduce((acc, p) => acc + p.score * p.weight, 0) / totalWeight;
}

// ---------------------------------------------------------------------------
// Block-scoped close tokens (§3.5.1). Keys are `<blockId>.<token>`.
// ---------------------------------------------------------------------------

export function closeValues(blocks: CompositionBlock[], answers: Answers): CloseValues {
  const byId = new Map(blocks.map((b) => [b.id, b]));
  const v: CloseValues = {};
  for (const block of blocks) {
    const a = answers[block.id];
    if (!a) continue;
    switch (block.type) {
      case 'choice': {
        const ans = a as ChoiceAnswer;
        const opt = block.content.options.find((o) => o.id === ans.optionId);
        if (opt) {
          v[`${block.id}.choice`] = opt.text;
          const hasCorrect = block.content.options.some((o) => o.correct === true);
          if (hasCorrect) v[`${block.id}.correct`] = opt.correct === true ? 'yes' : 'no';
        }
        break;
      }
      case 'rate-slider':
      case 'confidence': {
        const ans = a as RateSliderAnswer;
        v[`${block.id}.value`] = ans.value;
        // If this re-rates an upstream slider/confidence, expose before/after/delta.
        const upstream = block.ref ? byId.get(block.ref) : undefined;
        const upAns = block.ref ? answers[block.ref] : undefined;
        if (upstream && (upstream.type === 'rate-slider' || upstream.type === 'confidence') && upAns) {
          const before = (upAns as RateSliderAnswer).value;
          v[`${block.id}.before`] = before;
          v[`${block.id}.after`] = ans.value;
          const delta = ans.value - before;
          v[`${block.id}.delta`] = delta > 0 ? `+${delta}` : String(delta);
        }
        break;
      }
      case 'flag-in-text': {
        const ans = a as FlagInTextAnswer;
        const flaggedIds = new Set(block.content.segments.filter((s) => s.flagged === true).map((s) => s.id));
        v[`${block.id}.correct`] = ans.flagged.filter((id) => flaggedIds.has(id)).length;
        v[`${block.id}.total`] = flaggedIds.size;
        v[`${block.id}.picked`] = ans.flagged.length;
        break;
      }
      case 'sort-zones': {
        const ans = a as SortZonesAnswer;
        const items = block.content.items;
        v[`${block.id}.correct`] = items.filter((it) => ans.placement[it.id] === it.correctZone).length;
        v[`${block.id}.total`] = items.length;
        break;
      }
      case 'select-evidence': {
        const ans = a as SelectEvidenceAnswer;
        const strongIds = new Set(block.content.items.filter((it) => it.strong === true).map((it) => it.id));
        v[`${block.id}.correct`] = ans.selected.filter((id) => strongIds.has(id)).length;
        v[`${block.id}.total`] = strongIds.size;
        v[`${block.id}.picked`] = ans.selected.length;
        break;
      }
      case 'commit-box': {
        v[`${block.id}.text`] = (a as CommitBoxAnswer).text;
        break;
      }
      default:
        break;
    }
  }
  return v;
}

// ---------------------------------------------------------------------------
// The one composite capture payload per experience (§3.4). Mirrors the existing
// {before, after} pattern: per-block values inside `responseData.blocks`, plus
// the rollup. `score` is 0–100 for the gradebook.
// ---------------------------------------------------------------------------

export interface CompositeCapture {
  responseData: {
    version: 1;
    blocks: Record<string, { type: string; answer: BlockAnswer; score: number | null }>;
    rollup: number | null; // 0..1, or null for an ungraded (mode 'none') experience
  };
  score: number | null; // 0..100, or null when ungraded
}

export function buildCapture(
  blocks: CompositionBlock[],
  scoring: CompositionScoring,
  answers: Answers,
): CompositeCapture {
  const blockRecords: CompositeCapture['responseData']['blocks'] = {};
  for (const block of blocks) {
    const a = answers[block.id];
    if (!a) continue;
    blockRecords[block.id] = { type: block.type, answer: a, score: scoreBlock(block, a) };
  }
  // Ungraded experience (values / opinion / reflection): capture positions, no rollup.
  if (scoring.mode === 'none') {
    return { responseData: { version: 1, blocks: blockRecords, rollup: null }, score: null };
  }
  const rollup = rollupScore(blocks, scoring, answers);
  return {
    responseData: { version: 1, blocks: blockRecords, rollup },
    score: Math.round(rollup * 100),
  };
}
